import { useCallback, useState, useMemo } from "react";
import { CompositeAsset } from "../../../../common/api/decimal/common-types/manageAssets";
import useAssetActivate from "../../actions/explore/activate/useAssetActivate";
import useAssetReturn from "../../actions/explore/return/useAssetReturn";
import { PanelType } from "../asset-panel/types";
import usePopoverLinks from "../cards/usePopoverLinks";
import AssetPanel from "../asset-panel/AssetPanel.tsx";
import CoursewareDialog from "../../actions/courseware/CoursewareDialog.tsx";
import ContentResultsPanel from "../../search/book-content/ContentResultsPanel.tsx";
import OffScreen from "../../../../common/core-ui/OffScreen.tsx";
import { makeSearchReturnDetail } from "../../search/book-content/types.ts";

interface Opts {
  showContentSearch?: boolean;
  contentSearchSection?: string;
}

export default function useStandardAssetPopover(
  asset: CompositeAsset,
  opts: Opts = {}
) {
  const [openPanelInfo, setOpenPanelInfo] = useState<{
    time: number;
    startTab: PanelType;
  }>();
  const [searchOpenTime, setSearchOpenTime] = useState(0);
  const [coursewareDialogOpen, setCoursewareDialogOpen] = useState(false);
  const redeemCoursewareClose = useCallback(
    () => setCoursewareDialogOpen(false),
    []
  );
  const returnAsset = useAssetReturn(asset.explore);
  const activateAsset = useAssetActivate(asset.explore);

  const setOpenPanel = useCallback((startTab: PanelType) => {
    setOpenPanelInfo({ time: Date.now(), startTab });
  }, []);

  const popoverActions = useMemo(
    () => ({
      openCoursewareDialog: () => setCoursewareDialogOpen(true),
      openDetailsPanel: () => setOpenPanel(PanelType.Details),
      openAncillariesPanel: () => setOpenPanel(PanelType.Ancillaries),
      returnAsset,
      activateAsset,
      openContentSearchPanel: () => setSearchOpenTime(Date.now()),
      openAccessibilityPanel: () => setOpenPanel(PanelType.Accessibility),
    }),
    [returnAsset, activateAsset, setOpenPanel]
  );

  const popoverLinks = usePopoverLinks(asset, popoverActions, {
    showContentSearch: opts.showContentSearch,
  });

  /**
   * Needs to be rendered inside the card's li
   */
  const renderPopoverLinkDependencies = () => {
    return (
      <>
        {openPanelInfo && (
          <AssetPanel
            key={openPanelInfo.time}
            startTab={openPanelInfo.startTab}
            asset={asset.manageAsset}
            license={asset.license}
            explore={asset.explore}
            onRequestClose={() => setOpenPanelInfo(undefined)}
          />
        )}
        {coursewareDialogOpen && asset.license && (
          <CoursewareDialog
            asset={asset.manageAsset}
            onRequestClose={redeemCoursewareClose}
            license={asset.license}
          />
        )}
        {searchOpenTime > 0 && (
          <ContentResultsPanel
            onRequestClose={() => setSearchOpenTime(0)}
            // manageAsset won't show a search input without additional changes
            // Not currently a needed use case but go ahead and let it open
            // if that's what we have
            asset={asset.explore ?? asset.manageAsset}
            section={opts.contentSearchSection!}
          />
        )}
        {/** @see useReaderSearchReturn **/}
        {opts.showContentSearch && opts.contentSearchSection && (
          <OffScreen>
            <button
              tabIndex={-1}
              aria-hidden={true}
              data-library-return={makeSearchReturnDetail(
                opts.contentSearchSection,
                asset.manageAsset.vbid
              )}
              onClick={() => setSearchOpenTime(Date.now())}
            />
          </OffScreen>
        )}
      </>
    );
  };

  return {
    popoverLinks,
    renderPopoverLinkDependencies,
    popoverActions,
  };
}
